import { faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React, { ChangeEvent, ReactElement } from 'react';

import { paginationDefaultState } from '../../../../constants/paginationConstants';
import { useQueryParams } from '../../../../utils/useQueryParams';
import { TableQueryParameters } from '../Table.interface';
import classes from './TablePagination.module.scss';

export interface TablePaginationProps<S, T> {
  className?: string;
  total: number;
  limitLabel?: string;
  limitOptions?: number[];
  urlParams: TableQueryParameters<S, T>;
}

const TablePagination = <S, T>({
  total,
  className,
  limitLabel = 'Rows per page:',
  limitOptions = [10, 25, 50, 100],
  urlParams,
}: TablePaginationProps<S, T>): ReactElement => {
  const { updateURLWithQueryParams } = useQueryParams<typeof urlParams>(
    window.location.search,
    urlParams
  );

  const page = urlParams?.page || paginationDefaultState?.page;
  const limit = urlParams?.size || paginationDefaultState?.size;

  const totalPages = Math.ceil(total / limit);
  const previousPage = page > 0;
  const nextPage = page < totalPages - 1;
  const currentStart = page * limit + 1;
  const currentEnd = Math.min(page * limit + limit, total);

  const onPageChange = (page: number) => {
    const updatedParams = {
      ...urlParams,
      page: page,
    };
    updateURLWithQueryParams<typeof urlParams>(
      window.location.pathname,
      updatedParams
    );
  };

  const changeRowsPerPage = (event: ChangeEvent<HTMLSelectElement>) => {
    const updatedParams = {
      ...urlParams,
      page: 0,
      size: parseInt(event.target.value),
    };

    updateURLWithQueryParams<typeof urlParams>(
      window.location.pathname,
      updatedParams
    );
  };

  return (
    <div className={classNames(classes.TablePagination, className)}>
      <section>
        <div>
          <span>{limitLabel}</span>
          <select value={limit} onChange={changeRowsPerPage}>
            {limitOptions.map((value, i) => (
              <option key={i} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className={classes.Seperator} />
        <span>{`${currentStart}-${currentEnd} of ${total}`}</span>
      </section>
      <section>
        <div>
          <select
            value={page}
            onChange={(event) => {
              onPageChange(parseInt(event.target.value));
            }}
          >
            {totalPages
              ? [...Array(totalPages)].map((value, i) => (
                  <option key={i} value={i}>
                    {i + 1}
                  </option>
                ))
              : null}
          </select>
        </div>
        <div className={classes.Seperator} />
        <div
          className={classes.Icon}
          onClick={() => {
            if (previousPage) {
              onPageChange(page - 1);
            }
          }}
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </div>
        <div className={classes.Seperator} />
        <div
          className={classes.Icon}
          onClick={() => {
            if (nextPage) {
              onPageChange(page + 1);
            }
          }}
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </div>
      </section>
    </div>
  );
};

export default TablePagination;
